import React, {FC, ReactNode, useContext, useEffect, useRef} from 'react';
import styled from 'styled-components';

import {computeScrollbarWidth} from '../../../../../libs/shared/helpers/src/browser/domHelpers';
import {KeyboardActionsEnum} from '../../../../../libs/shared/helpers/src/browser/keyboardHelpers';
import {Trans} from '../../../../../libs/shared/helpers/src/internationalization/react';
import {HandlerOf} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {KeyboardShortcuts} from '../../../../../libs/shared/ui/src/components/keyboard/keyboardShortcuts';
import {fontSizes} from '../../../../../libs/shared/ui/src/styles/fontStyles';
import {center} from '../../../../../libs/shared/ui/src/styles/mixins';
import {useReconnectHandler} from '../../../../../srcCommon/push/pushContext';
import {logger} from '../../../../../srcCommon/services/logger/logger';
import {useAppDispatch, useAppSelector} from '../../../../helpers/reduxHelpers';
import {ConversationModel} from '../../../../models/api/conversationModel';
import {AppState} from '../../../../store/appState';
import {getConversations} from '../../../../store/data/dataSelectors';
import {getHasGrammarlyIntegration} from '../../../../store/featureFlags/featureFlagsSelectors';
import {ViewerPanelId} from '../../../../store/panels/panelStateBase';
import {
  FetchConversationCachePoliciesEnum,
  navigateViewerAsyncAction,
  refetchViewerAsyncAction,
} from '../../../../store/panels/viewer/viewerPanelActions';
import {ViewerPanelConversationPageState} from '../../../../store/panels/viewer/viewerPanelState';
import {GrammarlyContext} from '../../../grammarly/grammarlyContext';
import {GuidedDemoConversationViewerInfoBubble} from '../../../infoBubbles/guidedDemoConversationViewerInfoBubble';
import {CommentComposerContainer} from '../commentComposer/commentComposerContainer';
import {ConversationTimelineContainer} from '../conversation/timeline/conversationTimelineContainer';

/*
 * Props.
 */

interface SingleConversationViewerContentProps {
  panelId: ViewerPanelId;
  page: ViewerPanelConversationPageState;
  shouldFocusComment: boolean;
}

/*
 * Style.
 */

const StyledTimelineAndComposerWrapperDiv = styled.div`
  display: grid;
  grid-template-columns: 100%;
  grid-template-rows: minmax(33%, 1fr) auto;
  grid-template-areas:
    'timeline'
    'comment';
  grid-area: timeline-and-comment;
  background: ${(p) => p.theme.appBackground};
`;

const StyledNotFoundDiv = styled.div`
  ${center()};
  color: ${(p) => p.theme.greys.shade70};
  font-size: ${fontSizes.medium};
`;

/*
 * Component.
 */

export const SingleConversationViewerContent: FC<SingleConversationViewerContentProps> = (props) => {
  const {panelId, page} = props;
  const {conversationId} = page;

  const dispatch = useAppDispatch();
  const conversation = useAppSelector((state: AppState) => getConversations(state)[conversationId]);

  // We don't really expect this to happen in practice, so let's find out if that's true.
  useEffect(() => {
    if (!conversation) {
      logger.info('Single conversation viewer rendered without a conversation', {
        conversation_id: conversationId,
      });
    }
  }, [conversationId, conversation]);

  useReconnectHandler(() => {
    dispatch(refetchViewerAsyncAction(panelId));
  });

  const onForceRetchAction = () => {
    dispatch(
      navigateViewerAsyncAction(panelId, page.parameters, {
        cachePolicy: FetchConversationCachePoliciesEnum.NETWORK_ONLY,
      }),
    );
  };

  // Ref for info bubble.
  const timelineAndComposerWrapperRef = useRef<HTMLDivElement | null>(null);
  const {setComposerHeight, setScrollbarWidth} = useContext(GrammarlyContext);

  const scrollbarWidth = computeScrollbarWidth();
  const hasGrammarlyIntegration = useAppSelector(getHasGrammarlyIntegration);

  const conversationTimelineElement = hasGrammarlyIntegration
    ? timelineAndComposerWrapperRef.current?.querySelector('[data-testid="conversation-timeline"]')
    : undefined;
  const isConversationTimelineOverflowing =
    conversationTimelineElement &&
    conversationTimelineElement.scrollHeight > conversationTimelineElement.clientHeight;

  useEffect(() => {
    if (hasGrammarlyIntegration && isConversationTimelineOverflowing) {
      setScrollbarWidth(scrollbarWidth);
    } else {
      setScrollbarWidth(0);
    }
  }, [scrollbarWidth, isConversationTimelineOverflowing, hasGrammarlyIntegration]);

  return (
    <>
      <KeyboardShortcuts handlers={{[KeyboardActionsEnum.FORCE_REFETCH_CONVERSATION]: onForceRetchAction}} />
      <StyledTimelineAndComposerWrapperDiv
        ref={timelineAndComposerWrapperRef}
        data-testid={`viewer-${conversationId}`}
      >
        {renderTimelineAndComposer(props, conversation, setComposerHeight)}
      </StyledTimelineAndComposerWrapperDiv>
      <GuidedDemoConversationViewerInfoBubble targetRef={timelineAndComposerWrapperRef} />
    </>
  );
};

function renderTimelineAndComposer(
  props: SingleConversationViewerContentProps,
  conversation: ConversationModel | undefined,
  setComposerHeight?: HandlerOf<number>,
): ReactNode {
  // Just in case the conversation unexpectedly disappears before the viewer panel page does.
  if (!conversation) {
    return (
      <StyledNotFoundDiv>
        <Trans id="singleconversationviewercontent-notfound">This conversation can no longer be found</Trans>
      </StyledNotFoundDiv>
    );
  }

  return (
    <>
      <ConversationTimelineContainer
        panelId={props.panelId}
        page={props.page}
        conversation={conversation}
        key={`timeline-${props.page.uid}`}
      />
      <CommentComposerContainer
        panelId={props.panelId}
        cellsContext={props.page.cellsContext}
        conversationId={conversation.id}
        shouldFocus={props.shouldFocusComment}
        setComposerHeight={setComposerHeight}
      />
    </>
  );
}
